<template>
    <div class="iframe-container" :style="containerStyle">
        <iframe
            :id="id"
            ref="iframe"
            :name="id"
            :width="masterTemplateWidth"
            :height="masterTemplateHeight"
            frameborder="0"
            class="banner-iframe"
            :style="iframeStyle"
        />

        <form ref="postForm" :action="previewUrl" :target="id" method="POST">
            <input type="hidden" name="computedOverwrites" :value="computedOverwritesFormData" />
            <input type="hidden" name="metadata" :value="metadataFormData" />
        </form>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import GSAPTimeline from "gsap";
import throttle from "lodash.throttle";
import isAbsoluteUrl from "hoxton-shared/utils/isAbsoluteUrl";

import { PlaybackBannerEvents, PlaybackBannerActions } from "@/components/PlaybackControls/usePlaybackController";
import type { BannerPreviewData } from "@/types/types";
import bus from "@/bus";

export default defineComponent({
    props: {
        id: {
            required: true,
            type: String
        },
        scalingFactor: {
            type: Number,
            required: true
        },
        previewValues: {
            required: true,
            type: String
        },
        debug: {
            type: Boolean,
            default: false
        },
        playbackNamespace: {
            type: String,
            required: true
        }
    },

    emits: ["bannerLoaded", "loading"],

    data(): {
        iframe: Window | null;
        timeline: typeof GSAPTimeline | null;
    } {
        return {
            iframe: null,
            timeline: null
        };
    },

    computed: {
        data() {
            return JSON.parse(this.previewValues) as BannerPreviewData;
        },

        computedOverwritesFormData() {
            return JSON.stringify(this.data.computedOverwrites);
        },

        metadataFormData() {
            return JSON.stringify(this.data.metadata);
        },

        previewUrl() {
            if (isAbsoluteUrl(this.data.previewUrl)) {
                return this.data.previewUrl;
            }

            if (this.data.previewUrl.endsWith("/")) {
                return `${this.data.previewUrl}index.html?domain=${document.domain}`;
            }

            return `${this.data.previewUrl}/index.html?domain=${document.domain}`;
        },

        masterTemplateWidth() {
            return this.data.masterTemplate.width;
        },

        masterTemplateHeight() {
            return this.data.masterTemplate.height;
        },

        /**
         * if the scaling factor is less than 1, we want the container to fit the available space,
         * so it doesn't mess up the layout, even if the actual iframe is 'full size'
         */
        containerStyle() {
            if (this.scalingFactor < 1) {
                return {
                    width: `${this.masterTemplateWidth * this.scalingFactor}px`,
                    height: `${this.masterTemplateHeight * this.scalingFactor}px`
                };
            }

            return {};
        },

        /**
         * if the scaling factor is less than 1, we still want to 'render' the iframe at the full size,
         * but scale it down using CSS to fit the container
         *
         * scaling factor is only set if smaller than 1, so this doesn't apply if we're resizing up
         */
        iframeStyle() {
            if (this.scalingFactor < 1) {
                return {
                    transform: `scale(${this.scalingFactor})`,
                    transformOrigin: "0 0"
                };
            }

            /**
             * when not scaled, we want the iframe to fill the container,
             * even if the internal width/height are set to the master template width/height
             */
            return {
                width: "100%",
                height: "100%"
            };
        }
    },

    created() {
        bus.$emit(PlaybackBannerEvents.Init, { namespace: this.playbackNamespace, id: this.id });
    },

    mounted() {
        // If we add this as an attribute to the iframe tag then we get a
        //  "Function statements require a function name" error
        (this.$refs.iframe as HTMLIFrameElement).onload = this.iframeLoaded;
        (this.$refs.postForm as HTMLFormElement).submit();

        bus.$on(PlaybackBannerActions.ReplayBanner, this.onReplay);

        bus.$on(PlaybackBannerActions.Play, this.onPlay);
        bus.$on(PlaybackBannerActions.Pause, this.onPause);
        bus.$on(PlaybackBannerActions.Seek, this.onSeek);
    },

    beforeUnmount() {
        (this.$refs.iframe as HTMLIFrameElement).onload = () => undefined;

        bus.$off(PlaybackBannerActions.ReplayBanner, this.onReplay);

        bus.$off(PlaybackBannerActions.Play, this.onPlay);
        bus.$off(PlaybackBannerActions.Pause, this.onPause);
        bus.$off(PlaybackBannerActions.Seek, this.onSeek);

        bus.$emit(PlaybackBannerEvents.Remove, { namespace: this.playbackNamespace, id: this.id });
    },

    methods: {
        onReplay({ namespace, id }: { namespace: string; id?: string }) {
            if (id !== undefined && id !== this.id) {
                return;
            }

            if (namespace !== this.playbackNamespace) {
                return;
            }

            this.seek(0);
            this.play();
        },

        onPlay({ namespace, id }: { namespace: string; id?: string }) {
            if (id !== undefined && id !== this.id) {
                return;
            }

            if (namespace !== this.playbackNamespace) {
                return;
            }

            this.play();
        },

        onPause({ namespace, id }: { namespace: string; id?: string }) {
            if (id !== undefined && id !== this.id) {
                return;
            }

            if (namespace !== this.playbackNamespace) {
                return;
            }

            this.pause();
        },

        onSeek({ namespace, time, id }: { namespace: string; time: number; id?: string }) {
            if (id !== undefined && id !== this.id) {
                return;
            }

            if (namespace !== this.playbackNamespace) {
                return;
            }

            this.seek(time);
        },

        play() {
            if (this.timeline) {
                this.timeline.play();
            } else {
                // key++
            }
        },

        pause() {
            if (this.timeline) {
                this.timeline.pause();
            }
        },

        seek(time: number) {
            if (this.timeline) {
                this.timeline.seek(time, false);
            }
        },

        iframeLoaded(evt: Event) {
            // Disable alert and console and functions from inside the iframe to stop banner logs running in Hoxton
            try {
                const originalConsole = (this.$refs.iframe as HTMLIFrameElement).contentWindow!.console;

                (this.$refs.iframe as HTMLIFrameElement).contentWindow!.alert = () => "";
                (this.$refs.iframe as HTMLIFrameElement).contentWindow!.console = {
                    error: () => "",
                    info: () => "",
                    log: () => "",
                    warn: () => ""
                };

                if (this.debug && (this.$refs.iframe as HTMLIFrameElement)?.contentWindow) {
                    const bannerLabel = `🔔 ${this.masterTemplateWidth}x${this.masterTemplateHeight} - ${this.id}:`;

                    (this.$refs.iframe as HTMLIFrameElement).contentWindow!.console = {
                        error: (...args) => {
                            originalConsole.error(bannerLabel, ...args);
                        },
                        info: (...args) => {
                            originalConsole.info(bannerLabel, ...args);
                        },
                        log: (...args) => {
                            originalConsole.log(bannerLabel, ...args);
                        },
                        warn: (...args) => {
                            originalConsole.warn(bannerLabel, ...args);
                        }
                    };
                }
            } catch (e: unknown) {
                if ((e as Error).message.includes("Blocked a frame with origin")) {
                    console.error(e, {
                        "Parent origin": window.location.origin,
                        "Parent document domain": document.domain,
                        "Preview URL": this.previewUrl
                    });
                } else {
                    console.error(e);
                }
            }

            this.$emit("loading", false);
            this.$emit("bannerLoaded");

            this.iframe = evt.currentTarget?.contentWindow;
            this.timeline = this.iframe?.Creative?.tl ?? this.iframe?.TL;

            if (this.timeline) {
                this.timeline.eventCallback("onStart", () => {
                    if (!this.timeline) {
                        return;
                    }

                    bus.$emit(PlaybackBannerEvents.SetBannerDuration, {
                        namespace: this.playbackNamespace,
                        id: this.id,
                        duration: this.timeline.duration()
                    });
                });

                this.timeline.eventCallback(
                    "onUpdate",
                    throttle(() => {
                        if (!this.timeline) {
                            return;
                        }

                        bus.$emit(PlaybackBannerEvents.SetBannerTime, {
                            namespace: this.playbackNamespace,
                            id: this.id,
                            currentTime: this.timeline.time()
                        });

                        bus.$emit(PlaybackBannerEvents.SetBannerDuration, {
                            namespace: this.playbackNamespace,
                            id: this.id,
                            duration: this.timeline.duration()
                        });
                    }, 200)
                );

                this.play();
            }
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.iframe-container {
    flex: 1;

    position: relative;
}

.banner-iframe {
    margin: 0;
    padding: 0;

    position: absolute;
}
</style>
